import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CloudArrowUp, DownloadSimple, LinkSimple, Plus, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";

const TONE = {
  Match: "text-success", Tied: "text-success", "Reconciled within tolerance": "text-success",
  Difference: "text-[hsl(0_62%_44%)]", Error: "text-[hsl(0_62%_44%)]",
  Note: "text-[hsl(28_82%_38%)]",
};
const tone = (s) => TONE[s] || "text-navy";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="label-caps">{label}</span>
    {children}
  </label>
);

const input = "w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-navy outline-none focus:border-terracotta";

const SlotRow = ({ slot, canConfigure, onUpload, onLink, onSave, onDelete, tenantId }) => {
  const fileRef = useRef(null);
  const [edit, setEdit] = useState({ ...slot });
  const latest = slot.latest;
  return (
    <div className="panel p-5" data-testid={`fp-wb-slot-${slot.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps">{slot.role === "input" ? "Input" : "Expected output"} · {slot.code}</p>
          <h3 className="font-display text-lg text-navy">{slot.label}</h3>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Parser: {slot.parser}{slot.tie_control_item ? ` · ties to Tally control “${slot.tie_control_item}”` : " · no Tally tie"}
          </p>
          {latest ? (
            <p className="mt-2 text-xs text-navy" data-testid={`fp-wb-latest-${slot.code}`}>
              v{latest.version} · {latest.filename || latest.sheet_url} · {String(latest.uploaded_at).slice(0, 10)}
              {latest.filename && (
                <a className="ml-3 inline-flex items-center gap-1 text-terracotta"
                   data-testid={`fp-wb-download-${slot.code}`}
                   href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/workbooks/${slot.id}/download/${latest.id}`}>
                  <DownloadSimple size={12} /> download
                </a>
              )}
            </p>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">Nothing attached yet</p>
          )}
        </div>
        {canConfigure && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="file" ref={fileRef} className="hidden" accept=".xlsx,.xlsm,.csv"
                   data-testid={`fp-wb-file-${slot.code}`}
                   onChange={(e) => e.target.files?.[0] && onUpload(slot, e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} data-testid={`fp-wb-upload-${slot.code}`}
                    className="inline-flex items-center gap-2 rounded-full bg-terracotta px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-white hover:bg-terracotta/90">
              <CloudArrowUp size={13} /> Attach Excel
            </button>
            <button onClick={() => onLink(slot)} data-testid={`fp-wb-link-${slot.code}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy hover:bg-muted">
              <LinkSimple size={13} /> Google Sheet
            </button>
            <button onClick={() => onDelete(slot)} data-testid={`fp-wb-delete-${slot.code}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-border text-navy hover:bg-muted">
              <Trash size={13} />
            </button>
          </div>
        )}
      </div>

      {canConfigure && (
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-4">
          <Field label="Label">
            <input className={input} value={edit.label} data-testid={`fp-wb-label-${slot.code}`}
                   onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
          </Field>
          <Field label="Role">
            <select className={input} value={edit.role} data-testid={`fp-wb-role-${slot.code}`}
                    onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
              <option value="input">Input</option>
              <option value="expected_output">Expected output</option>
            </select>
          </Field>
          <Field label="Parser">
            <select className={input} value={edit.parser} data-testid={`fp-wb-parser-${slot.code}`}
                    onChange={(e) => setEdit({ ...edit, parser: e.target.value })}>
              {["none", "revenue", "pvc", "dvc", "fixed_cost", "balance_sheet", "generic"]
                .map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Ties to Tally control item">
            <input className={input} value={edit.tie_control_item || ""}
                   data-testid={`fp-wb-tie-${slot.code}`} placeholder="e.g. Revenue"
                   onChange={(e) => setEdit({ ...edit, tie_control_item: e.target.value })} />
          </Field>
          <div className="sm:col-span-4">
            <button onClick={() => onSave(slot, edit)} data-testid={`fp-wb-save-${slot.code}`}
                    className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy hover:bg-muted">
              Save slot
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function FpWorkbooks() {
  const { tenantId, period, basis, reloadContext } = useFpContext();
  const [reg, setReg] = useState(null);
  const [gap, setGap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newSlot, setNewSlot] = useState({ code: "", label: "", role: "input", parser: "none", tie_control_item: "" });

  const [svd, setSvd] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSvd = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/svd-pack`);
      setSvd(data);
    } catch (e) { setSvd({ error: errMsg(e) }); }
  }, [tenantId]);

  useEffect(() => { loadSvd(); }, [loadSvd]);

  const loadReg = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/workbooks`);
      setReg(data);
    } catch (e) { setReg({ error: errMsg(e) }); }
  }, [tenantId]);

  const loadGap = useCallback(async () => {
    if (!tenantId || !period) return;
    setBusy(true);
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/output-pack/gap`,
        { params: { period, basis } });
      setGap(data);
    } catch (e) { setGap({ error: errMsg(e) }); }
    setBusy(false);
  }, [tenantId, period, basis]);

  useEffect(() => { loadReg(); }, [loadReg]);
  useEffect(() => { loadGap(); }, [loadGap]);

  if (!reg) {
    return <FpShell title="Input & output workbooks">
      <p className="label-caps animate-pulse">Loading the workbook registry</p>
    </FpShell>;
  }
  if (reg.error) {
    return <FpShell title="Input & output workbooks">
      <p className="panel p-6 text-sm text-navy">{reg.error}</p>
    </FpShell>;
  }

  const canConfigure = reg.can_configure;
  const settings = gap?.settings || reg.settings;

  const upload = async (slot, file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("period", period || "");
    try {
      await api.post(`${fpBase(tenantId)}/workbooks/${slot.id}/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached to ${slot.label}`);
      loadReg(); loadGap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const link = async (slot) => {
    const url = window.prompt(`Google Sheet share link for ${slot.label}`);
    if (!url) return;
    try {
      await api.post(`${fpBase(tenantId)}/workbooks/${slot.id}/link`, { sheet_url: url, period: period || "" });
      toast.success("Sheet linked");
      loadReg(); loadGap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveSlot = async (slot, edit) => {
    try {
      await api.put(`${fpBase(tenantId)}/workbooks/${slot.id}`, {
        code: slot.code, label: edit.label, role: edit.role, parser: edit.parser,
        tie_control_item: edit.tie_control_item || "", period: edit.period || "", notes: edit.notes || "",
      });
      toast.success("Slot saved"); loadReg(); loadGap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeSlot = async (slot) => {
    if (!window.confirm(`Remove the slot “${slot.label}” and its attachments?`)) return;
    try {
      await api.delete(`${fpBase(tenantId)}/workbooks/${slot.id}`);
      toast.success("Slot removed"); loadReg(); loadGap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addSlot = async () => {
    try {
      await api.post(`${fpBase(tenantId)}/workbooks`, newSlot);
      setNewSlot({ code: "", label: "", role: "input", parser: "none", tie_control_item: "" });
      toast.success("Slot added"); loadReg();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runMaterialise = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`${fpBase(tenantId)}/workbooks/materialise`, null,
        { params: { period } });
      toast.success(`Loaded ${data.loaded.length} sources into the engine for ${data.period}`);
      loadReg(); loadGap(); loadSvd(); reloadContext?.();
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const saveSettings = async (patch) => {    try {
      const { data } = await api.put(`${fpBase(tenantId)}/workbooks/settings`, { ...settings, ...patch });
      toast.success(`Tie rule saved: ${data.tie_mode === "error" ? "difference is an error" : "difference is a note"}`);
      loadReg(); loadGap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const q = period ? `?period=${period}&basis=${basis}` : "";
  const base = `${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}`;

  return (
    <FpShell
      title="Input & output workbooks"
      description="Attach the client's input sheets and their expected output pack, per engagement. The engine generates its own output pack and reconciles it line by line against what the client expects — and every input sheet against the Tally books."
      actions={
        <div className="flex flex-wrap gap-2">
          <a href={`${base}/output-pack.xlsx${q}`} data-testid="fp-wb-download-pack"
             className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
            <DownloadSimple size={13} /> Generate output pack
          </a>
          <a href={`${base}/output-pack/gap.xlsx${q}`} data-testid="fp-wb-download-gap"
             className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
            <DownloadSimple size={13} /> Gap report
          </a>
          {reg.can_configure && (
            <a href={`${base}/audit-pack.xlsx${q}`} data-testid="fp-wb-download-audit-pack"
               className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
              <DownloadSimple size={13} /> Audit pack
            </a>
          )}
          <button onClick={loadGap} data-testid="fp-wb-refresh"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
            <ArrowsClockwise size={13} /> Re-reconcile
          </button>
          {reg.can_configure && (
            <button onClick={runMaterialise} disabled={loading} data-testid="fp-wb-materialise"
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90 disabled:opacity-60">
              <CloudArrowUp size={13} /> {loading ? "Loading…" : "Load into the engine"}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6" data-testid="fp-workbooks">
        <div className="panel px-6 py-4" data-testid="fp-wb-client-downloads">
          <p className="label-caps mb-3">Client templates &amp; extracts</p>
          <div className="flex flex-wrap gap-2">
            {[
              ["P&L mapping template", "/Aikyam-FPE-Mapping-Template-SVS.xlsx", "fp-dl-mapping-template"],
              ["Sales register template", "/Aikyam-FPE-Sales-Register-SVS.xlsx", "fp-dl-sales-register"],
              ["Employee master template", "/Aikyam-FPE-Employee-Master-SVS.xlsx", "fp-dl-employee-master"],
              ["Power & fuel template", "/Aikyam-FPE-Power-Fuel-Template-SVS.xlsx", "fp-dl-power-fuel"],
              ["Fixed cost line by line (Jul 2026)", "/Aikyam-FPE-Fixed-Cost-SVS-Jul2026.xlsx", "fp-dl-fixed-cost"],
              ["Production VC line by line (Jul 2026)", "/Aikyam-FPE-Production-VC-SVS-Jul2026.xlsx", "fp-dl-production-vc"],
              ["Distribution VC line by line (Jul 2026)", "/Aikyam-FPE-Distribution-VC-SVS-Jul2026.xlsx", "fp-dl-distribution-vc"],
              ["Enhancement notes (Word)", "/Aikyam-FPE-Next-Steps.docx", "fp-dl-next-steps"],
            ].map(([label, href, tid]) => (
              <a key={tid} href={href} download data-testid={tid}
                 className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-[11px] text-navy hover:bg-muted">
                <DownloadSimple size={12} /> {label}
              </a>
            ))}
          </div>
        </div>

        <div className="panel p-6" data-testid="fp-wb-settings">
          <p className="label-caps">Tie rule for this client</p>
          <h3 className="font-display text-lg text-navy">Do the attached sheets have to tie to the Tally books?</h3>
          <div className="mt-4 flex flex-wrap items-end gap-6">
            <div className="flex overflow-hidden rounded-full border border-border">
              {[["error", "Difference is an error"], ["note", "Difference is a note"]].map(([k, l]) => (
                <button key={k} disabled={!canConfigure} data-testid={`fp-wb-tie-mode-${k}`}
                        onClick={() => saveSettings({ tie_mode: k })}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                          settings?.tie_mode === k ? "bg-terracotta text-white" : "bg-card text-navy hover:bg-muted"}`}>
                  {l}
                </button>
              ))}
            </div>
            <Field label="Overall tolerance ₹">
              <input type="number" className={`${input} w-40`} defaultValue={settings?.tolerance_total}
                     data-testid="fp-wb-tolerance"
                     disabled={!canConfigure}
                     onBlur={(e) => saveSettings({ tolerance_total: Number(e.target.value) || 0 })} />
            </Field>
            <div className="flex items-center gap-3">
              <button onClick={() => saveSettings({ auto_load: !settings?.auto_load })}
                      disabled={!canConfigure} data-testid="fp-wb-auto-load"
                      className={`rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                        settings?.auto_load ? "bg-success/20 text-success" : "bg-muted text-navy"}`}>
                {settings?.auto_load ? "Auto-load on upload: on" : "Auto-load on upload: off"}
              </button>
              <p className="max-w-xs text-[11px] text-muted-foreground">
                With this on, attaching a workbook rebuilds this month in the engine straight away,
                in whichever environment you uploaded it to.
              </p>
            </div>
            <p className="max-w-md text-[11px] text-muted-foreground">
              Anything beyond the tolerance is flagged
              {settings?.tie_mode === "error" ? " as an error and blocks sign-off" : " as a note only"}.
            </p>
          </div>
        </div>

        {[
          ["monthly", "Every month", "The standing monthly package — attach these at each month-end and the engine does the rest."],
          ["setup", "One-time setup & reference", "Mapping masters, reconciliation packs and occasional inputs — set once, revise when things change."],
        ].map(([section, title, blurb]) => {
          const slots = (reg.slots || [])
            .filter((s) => (s.section || "monthly") === section)
            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
          if (!slots.length) return null;
          return (
            <div key={section} data-testid={`fp-wb-section-${section}`}>
              <p className="label-caps">{title}</p>
              <p className="mb-3 mt-1 text-[11px] text-muted-foreground">{blurb}</p>
              {section === "monthly" && (() => {
                const inPeriod = (s) => (s.versions || []).some((v) => !period || v.period === period);
                const done = slots.filter(inPeriod);
                const pending = slots.filter((s) => !inPeriod(s));
                return (
                  <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="fp-wb-checklist">
                    <span className={`rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] ${
                      pending.length ? "bg-warning/15 text-[hsl(30_88%_28%)]" : "bg-success/20 text-success"}`}
                      data-testid="fp-wb-checklist-count">
                      {done.length} of {slots.length} monthly files in{period ? ` · ${monthLabel(period)}` : ""}
                    </span>
                    {pending.map((s) => (
                      <span key={s.code} data-testid={`fp-wb-checklist-pending-${s.code}`}
                            className="rounded-full border border-border px-3 py-1 text-[11px] text-muted-foreground">
                        {s.label} — pending
                      </span>
                    ))}
                    {!pending.length && (
                      <span className="text-[11px] font-semibold text-success"
                            data-testid="fp-wb-checklist-complete">
                        Monthly package complete — ready to load into the engine.
                      </span>
                    )}
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {slots.map((s) => (
                  <SlotRow key={s.id} slot={s} canConfigure={canConfigure} tenantId={tenantId}
                           onUpload={upload} onLink={link} onSave={saveSlot} onDelete={removeSlot} />
                ))}
              </div>
            </div>
          );
        })}

        {canConfigure && (
          <div className="panel p-6" data-testid="fp-wb-add">
            <p className="label-caps">Add a workbook slot</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-5">
              <Field label="Code">
                <input className={input} value={newSlot.code} data-testid="fp-wb-new-code"
                       onChange={(e) => setNewSlot({ ...newSlot, code: e.target.value })} />
              </Field>
              <Field label="Label">
                <input className={input} value={newSlot.label} data-testid="fp-wb-new-label"
                       onChange={(e) => setNewSlot({ ...newSlot, label: e.target.value })} />
              </Field>
              <Field label="Role">
                <select className={input} value={newSlot.role} data-testid="fp-wb-new-role"
                        onChange={(e) => setNewSlot({ ...newSlot, role: e.target.value })}>
                  <option value="input">Input</option>
                  <option value="expected_output">Expected output</option>
                </select>
              </Field>
              <Field label="Parser">
                <select className={input} value={newSlot.parser} data-testid="fp-wb-new-parser"
                        onChange={(e) => setNewSlot({ ...newSlot, parser: e.target.value })}>
                  {(reg.parsers || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Ties to control item">
                <input className={input} value={newSlot.tie_control_item} data-testid="fp-wb-new-tie"
                       onChange={(e) => setNewSlot({ ...newSlot, tie_control_item: e.target.value })} />
              </Field>
            </div>
            <button onClick={addSlot} data-testid="fp-wb-new-save"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90">
              <Plus size={13} /> Add slot
            </button>
          </div>
        )}

        <div className="panel p-6" data-testid="fp-wb-svd">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="label-caps">Generated from the client's own inputs</p>
              <h3 className="font-display text-lg text-navy">
                SVD pack — Tally P&amp;L, stock, wastage, POS revenue and ledger config
              </h3>
            </div>
            {svd?.reconciliation && (
              <div className="text-right">
                <p className="label-caps">Gap vs the client's FP Summary</p>
                <p className={`font-mono text-lg font-semibold tabular-nums ${
                  svd.reconciliation.within_tolerance ? "text-success" : "text-[hsl(0_62%_44%)]"}`}
                   data-testid="fp-wb-svd-gap">{inr(svd.reconciliation.gap_total)}</p>
              </div>
            )}
          </div>

          {svd?.error && <p className="mt-3 text-sm text-[hsl(28_82%_38%)]">{svd.error}</p>}
          {svd?.reconciliation && (
            <>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="label-caps text-left">
                      <th className="py-2">Line</th>
                      <th className="text-right">Generated by the engine</th>
                      <th className="text-right">Client's pack</th>
                      <th className="text-right">Difference</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {svd.reconciliation.lines.map((l) => (
                      <tr key={l.line} className="border-t border-border"
                          data-testid={`fp-wb-svd-row-${l.line.toLowerCase().replace(/\W+/g, "-")}`}>
                        <td className="py-2 text-navy">{l.line}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.generated)}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.expected)}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.difference)}</td>
                        <td className={`font-semibold ${tone(l.status)}`}>{l.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!!(svd.mapping_exceptions || []).length && (
                <div className="mt-5 border-t border-border pt-4" data-testid="fp-wb-svd-exceptions">
                  <p className="label-caps">
                    {svd.mapping_exceptions.length} ledgers the Ledger Config does not cover ·
                    {" "}{inr(svd.mapping_exceptions.reduce((s, e) => s + (e.amount || 0), 0))}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {svd.mapping_exceptions.slice(0, 12).map((e, i) => (
                      <span key={i} className="rounded-full bg-warning/15 px-3 py-1 text-[11px] text-[hsl(30_88%_28%)]">
                        {e.ledger} · {inr(e.amount)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="panel p-6" data-testid="fp-wb-tally-ties">
          <p className="label-caps">Input sheets against the Tally books</p>
          <h3 className="font-display text-lg text-navy">Tally tie check</h3>
          {!(gap?.tally_ties || []).length ? (
            <p className="mt-3 text-sm text-muted-foreground">
              No input slot with both a parser and a Tally control item is attached yet.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="label-caps text-left">
                    <th className="py-2">Input sheet</th><th>Control item</th>
                    <th className="text-right">Tally</th><th className="text-right">Sheet</th>
                    <th className="text-right">Difference</th><th>Status</th><th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {gap.tally_ties.map((t) => (
                    <tr key={t.code} className="border-t border-border" data-testid={`fp-wb-tie-row-${t.code}`}>
                      <td className="py-2 text-navy">{t.slot}</td>
                      <td>{t.control_item}</td>
                      <td className="text-right font-mono tabular-nums">{inr(t.control_total)}</td>
                      <td className="text-right font-mono tabular-nums">{inr(t.sheet_total)}</td>
                      <td className="text-right font-mono tabular-nums">{inr(t.difference)}</td>
                      <td className={`font-semibold ${tone(t.status)}`}>{t.status}</td>
                      <td className="text-muted-foreground">{t.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="panel p-6" data-testid="fp-wb-gap">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="label-caps">Generated pack vs the client's expected pack</p>
              <h3 className="font-display text-lg text-navy">Gap reconciliation</h3>
            </div>
            {gap && !gap.error && (
              <div className="text-right">
                <p className="label-caps">Total gap</p>
                <p className={`font-mono text-lg font-semibold tabular-nums ${
                  gap.within_tolerance ? "text-success" : "text-[hsl(0_62%_44%)]"}`}
                   data-testid="fp-wb-gap-total">{inr(gap.gap_total)}</p>
                <p className={`text-[11px] ${tone(gap.verdict)}`} data-testid="fp-wb-verdict">{gap.verdict}</p>
              </div>
            )}
          </div>

          {busy && <p className="mt-3 label-caps animate-pulse">Reconciling</p>}
          {gap?.error && <p className="mt-3 text-sm text-[hsl(0_62%_44%)]">{gap.error}</p>}
          {gap && !gap.error && !gap.attached && (
            <p className="mt-3 text-sm text-muted-foreground">{gap.message}</p>
          )}

          {gap?.attached && (
            <>
              <p className="mt-3 text-[11px] text-muted-foreground">
                Comparing against {gap.source.filename} (v{gap.source.version}) ·
                {" "}{gap.counts.match} matched · {gap.counts.difference} differences ·
                {" "}{gap.counts.missing} missing · {gap.counts.extra} extra
                {gap.formula_cells_skipped ? ` · ${gap.formula_cells_skipped} formula cells skipped` : ""}
                {gap.unmatched_sheets?.length ? ` · sheets not compared: ${gap.unmatched_sheets.join(", ")}` : ""}
              </p>
              <div className="mt-4 max-h-[28rem] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card">
                    <tr className="label-caps text-left">
                      <th className="py-2">Sheet</th><th>Line</th><th>Column</th>
                      <th className="text-right">Generated</th><th className="text-right">Expected</th>
                      <th className="text-right">Difference</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gap.lines.map((l, i) => (
                      <tr key={i} className="border-t border-border" data-testid={`fp-wb-gap-row-${i}`}>
                        <td className="py-2 text-navy">{l.sheet}</td>
                        <td>{l.line}</td>
                        <td className="text-muted-foreground">{l.column}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.generated)}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.expected)}</td>
                        <td className="text-right font-mono tabular-nums">{inr(l.difference)}</td>
                        <td className={`font-semibold ${tone(l.status)}`}>{l.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </FpShell>
  );
}
